import { asWidgetProps } from 'Src/types/widget-helpers';
import React, { ComponentProps, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { classnames } from 'Utils/classnames/classnames.utils';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { ImmersiveSlideShowControls } from 'Components/widgets/immersive-slideshow/controls/ImmersiveSlideShowControls';
type Props = {
  className?: string;
  items: Array<React.ReactNode>;
  nextLabel?: string;
  previousLabel?: string;
  controlsTheme?: string;
  isPaginationDisabled?: boolean;
};
const PRELOADED_ITEMS_NB = 3;

const getInitialDisplayedIndex = (length: number) =>
  Array(length).fill(false).fill(true, 0, PRELOADED_ITEMS_NB);

export const ImmersiveSlideShowNarrow = ({
  className,
  items = [],
  nextLabel,
  previousLabel,
  isPaginationDisabled = true,
  controlsTheme = 'default',
}: Props) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [displayedIndex, setDisplayedIndex] = useState(getInitialDisplayedIndex(items.length));
  const { isRTL } = useResponsive();
  const coef = isRTL ? -1 : 1;
  const containerRef = useRef(null);

  const scroll = (index, e) => {
    if (e) {
      e.preventDefault();
    }

    const isFirst = activeIndex === 0;
    const isLast = activeIndex === items.length - 1;

    if (isFirst && index < activeIndex) {
      index = items.length - 1;
    }

    if (isLast && index > activeIndex) {
      index = 0;
    }

    if (containerRef && containerRef.current) {
      containerRef.current.scrollTo({
        behavior: 'smooth',
        left: coef * containerRef.current.scrollWidth * (index / items.length),
      });
    }

    setActiveIndex(index);
  };

  const onScroll = debounce(() => {
    if (containerRef && containerRef.current) {
      const pos = Math.round(
        ((coef * containerRef.current.scrollLeft) / containerRef.current.scrollWidth) *
          items.length,
      );
      setActiveIndex(pos);
    }
  }, 150);
  useEffect(() => {
    const container = containerRef.current;
    container?.addEventListener('scroll', onScroll);
    return () => {
      container?.removeEventListener('scroll', onScroll);
    };
  }, [coef]);
  useEffect(() => {
    if (displayedIndex.every(Boolean)) {
      return;
    }

    const newDisplayedIndex = displayedIndex.map(
      (_, idx) => idx < activeIndex + PRELOADED_ITEMS_NB,
    );
    setDisplayedIndex(newDisplayedIndex);
  }, [activeIndex]);

  if (!items.length) {
    return null;
  }

  return (
    <div className={classnames('relative overflow-hidden', className)}>
      <div
        className={classnames(
          'flex w-full snap-x snap-mandatory flex-row flex-nowrap overflow-auto',
          className,
        )}
        dir={isRTL ? 'rtl' : 'ltr'}
        ref={containerRef}
      >
        {items.map((item, idx) => (
          <div className="w-full flex-none snap-center overflow-visible" key={idx} tabIndex={-1}>
            {displayedIndex[idx] ? item : null}
          </div>
        ))}
      </div>
      {items.length > 1 && (
        <ImmersiveSlideShowControls
          {...asWidgetProps<ComponentProps<typeof ImmersiveSlideShowControls>>({
            activeIndex: activeIndex + 1,
            isPaginationDisabled: isPaginationDisabled,
            nextLabel: nextLabel,
            onNext: (e) => scroll(activeIndex + coef, e),
            onPrev: (e) => scroll(activeIndex - coef, e),
            previousLabel: previousLabel,
            slidesCount: items.length,
            theme: controlsTheme,
          })}
        />
      )}
    </div>
  );
};
